import { api, bff, publicApi } from './http'
import type {
  CreateInviteResponse,
  DigitooSendRequest,
  DigitooSendResult,
  ExportRequest,
  ExportSummary,
  ImportBatch,
  MailSettings,
  MovementDetail,
  MovementFilter,
  MovementPage,
  MovementStats,
  MovementStatus,
  OdometerHint,
  UpdateMailSettings,
  TravelRatesTable,
  Trip,
  TripList,
  TripRequest,
  TripSuggestions,
  User,
  Vehicle,
  VehicleFileKind,
  VehicleRequest,
} from './types'

// --- auth (BFF session + backend identity) ---
export const authApi = {
  login: (email: string, password: string, rememberMe = true) =>
    bff.post('/auth/login', { email, password, rememberMe }),
  logout: () => bff.post('/auth/logout'),
  me: () => api.get<User>('/app/users/me').then((r) => r.data),
}

// --- public invite ---
export const inviteApi = {
  info: (token: string) =>
    publicApi.get<{ email: string; firstName: string | null; lastName: string | null }>(`/invite/${token}`).then((r) => r.data),
  redeem: (token: string, password: string) => publicApi.post('/invite/redeem', { token, password }),
}

// --- users ---
export const usersApi = {
  list: () => api.get<User[]>('/app/users').then((r) => r.data),
  createInvite: (body: { email: string; firstName?: string; lastName?: string; isAdmin?: boolean }) =>
    api.post<CreateInviteResponse>('/app/users', body).then((r) => r.data),
  update: (id: string, body: Partial<Pick<User, 'firstName' | 'lastName' | 'isAdmin' | 'status'>>) =>
    api.patch<User>(`/app/users/${id}`, body).then((r) => r.data),
}

// --- bankovní pohyby ---
export const movementsApi = {
  search: (filter: MovementFilter) =>
    api.get<MovementPage>('/app/movements', { params: clean(filter) }).then((r) => r.data),
  stats: () => api.get<MovementStats>('/app/movements/stats').then((r) => r.data),
  detail: (id: string) => api.get<MovementDetail>(`/app/movements/${id}`).then((r) => r.data),
  update: (id: string, body: { status?: MovementStatus; note?: string }) =>
    api.patch<MovementDetail>(`/app/movements/${id}`, body).then((r) => r.data),
}

// --- doklady ---
export const filesApi = {
  upload: (movementId: string, files: File[], onProgress?: (pct: number) => void) => {
    const form = new FormData()
    files.forEach((f) => form.append('files', f, f.name))
    return api
      .post<MovementDetail>(`/app/movements/${movementId}/files`, form, {
        onUploadProgress: (e) => e.total && onProgress?.(Math.round((e.loaded / e.total) * 100)),
      })
      .then((r) => r.data)
  },
  remove: (fileId: string) => api.delete<MovementDetail>(`/app/files/${fileId}`).then((r) => r.data),
  /** Odkaz pro <a href> — stáhne se s session cookie, BFF přidá JWT. */
  url: (fileId: string, inline = false) =>
    `${api.defaults.baseURL}/app/files/${fileId}${inline ? '?inline=true' : ''}`,
}

// --- import ---
export const importsApi = {
  upload: (file: File) => {
    const form = new FormData()
    form.append('file', file, file.name)
    return api.post<ImportBatch>('/app/imports', form).then((r) => r.data)
  },
  history: () => api.get<ImportBatch[]>('/app/imports').then((r) => r.data),
}

// --- export ---
export const exportApi = {
  summary: (body: ExportRequest) => api.post<ExportSummary>('/app/export/summary', body).then((r) => r.data),
  zip: (body: ExportRequest) =>
    api.post<Blob>('/app/export/zip', body, { responseType: 'blob' }).then((r) => ({
      blob: r.data,
      fileName: fileNameFrom(r.headers['content-disposition']) ?? 'doklady.zip',
    })),
}

// --- odeslání do Digitoo ---
export const digitooApi = {
  send: (body: DigitooSendRequest) =>
    api.post<DigitooSendResult>('/app/digitoo/send', { ...body, filter: body.filter && clean(body.filter) }).then((r) => r.data),
  sendFile: (fileId: string) => api.post<MovementDetail>(`/app/digitoo/files/${fileId}`).then((r) => r.data),
}

// --- nastavení (admin) ---
export const settingsApi = {
  mail: () => api.get<MailSettings>('/app/settings/mail').then((r) => r.data),
  saveMail: (body: UpdateMailSettings) => api.put<MailSettings>('/app/settings/mail', body).then((r) => r.data),
  testMail: () => api.post<{ message: string }>('/app/settings/mail/test').then((r) => r.data),
}

// --- vozový park ---
export const vehiclesApi = {
  list: () => api.get<Vehicle[]>('/app/vehicles').then((r) => r.data),
  detail: (id: string) => api.get<Vehicle>(`/app/vehicles/${id}`).then((r) => r.data),
  create: (body: VehicleRequest) => api.post<Vehicle>('/app/vehicles', body).then((r) => r.data),
  update: (id: string, body: VehicleRequest) => api.put<Vehicle>(`/app/vehicles/${id}`, body).then((r) => r.data),
  remove: (id: string) => api.delete(`/app/vehicles/${id}`),
  rates: () => api.get<TravelRatesTable>('/app/vehicles/rates').then((r) => r.data),
  upload: (id: string, kind: VehicleFileKind, files: File[]) => {
    const form = new FormData()
    files.forEach((f) => form.append('files', f, f.name))
    return api.post<Vehicle>(`/app/vehicles/${id}/files`, form, { params: { kind } }).then((r) => r.data)
  },
  removeFile: (fileId: string) => api.delete<Vehicle>(`/app/vehicles/files/${fileId}`).then((r) => r.data),
  fileUrl: (fileId: string, inline = false) =>
    `${api.defaults.baseURL}/app/vehicles/files/${fileId}${inline ? '?inline=true' : ''}`,
}

// --- kniha jízd ---
export const tripsApi = {
  search: (filter: { vehicleId?: string; from?: string; to?: string }) =>
    api.get<TripList>('/app/trips', { params: clean(filter) }).then((r) => r.data),
  detail: (id: string) => api.get<Trip>(`/app/trips/${id}`).then((r) => r.data),
  create: (body: TripRequest) => api.post<Trip>('/app/trips', body).then((r) => r.data),
  update: (id: string, body: TripRequest) => api.put<Trip>(`/app/trips/${id}`, body).then((r) => r.data),
  remove: (id: string) => api.delete(`/app/trips/${id}`),
  suggestions: () => api.get<TripSuggestions>('/app/trips/suggestions').then((r) => r.data),
  odometer: (q: { vehicleId: string; date: string; tripId?: string }) =>
    api.get<OdometerHint>('/app/trips/odometer', { params: clean(q) }).then((r) => r.data),
  upload: (id: string, files: File[]) => {
    const form = new FormData()
    files.forEach((f) => form.append('files', f, f.name))
    return api.post<Trip>(`/app/trips/${id}/files`, form).then((r) => r.data)
  },
  removeFile: (fileId: string) => api.delete<Trip>(`/app/trips/files/${fileId}`).then((r) => r.data),
  fileUrl: (fileId: string, inline = false) =>
    `${api.defaults.baseURL}/app/trips/files/${fileId}${inline ? '?inline=true' : ''}`,
  pdf: (ids: string[]) =>
    api.post<Blob>('/app/trips/pdf', { ids }, { responseType: 'blob' }).then((r) => ({
      blob: r.data,
      fileName: fileNameFrom(r.headers['content-disposition']) ?? 'kniha-jizd.pdf',
    })),
}

/** Prázdné hodnoty z formuláře do query nepatří. */
function clean<T extends object>(obj: T): Partial<T> {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== '' && v !== null)) as Partial<T>
}

function fileNameFrom(header: string | undefined): string | null {
  if (!header) return null
  const star = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (star) return decodeURIComponent(star[1])
  const plain = /filename="([^"]+)"/i.exec(header)
  return plain ? plain[1] : null
}
